// Plan 15-07 Task 1 — FriendRequestRow.
// Phase 17 Plan 06 Task 1 (cleanup-reskin) — tokens + raw <button> kept
// to preserve data-cta="friend-request-accept|reject" test selectors.

import type { components } from '@runegambit/api-types'
import { type JSX, useCallback, useState } from 'react'
import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl'
import { acceptFriendRequest, rejectFriendRequest } from '../../net/friends.js'
import { Avatar } from '../components/Avatar'
import { Btn } from '../components/Btn'

export type FriendRequestInboxEntry = components['schemas']['FriendRequest']

export interface FriendRequestRowProps {
  readonly row: FriendRequestInboxEntry
  readonly onAccept?: (request_id: string) => void
  readonly onReject?: (request_id: string) => void
}

const FADE_DURATION_MS = 200

function dispatchFriendsRefresh(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('runegambit:friend-request-accepted'))
}

export function FriendRequestRow({
  row,
  onAccept,
  onReject,
}: FriendRequestRowProps): JSX.Element {
  const intl = useIntl()
  const [busy, setBusy] = useState(false)
  const [phase, setPhase] = useState<'idle' | 'accept-flash' | 'reject-fade'>(
    'idle',
  )

  const handleAccept = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      await acceptFriendRequest(row.request_id)
      setPhase('accept-flash')
      dispatchFriendsRefresh()
      setTimeout(() => {
        setPhase('idle')
        onAccept?.(row.request_id)
      }, FADE_DURATION_MS)
    } catch {
      setPhase('idle')
    } finally {
      setBusy(false)
    }
  }, [busy, row.request_id, onAccept])

  const handleReject = useCallback(async () => {
    if (busy) return
    setBusy(true)
    setPhase('reject-fade')
    try {
      await rejectFriendRequest(row.request_id)
      dispatchFriendsRefresh()
    } catch {
      setPhase('idle')
      setBusy(false)
      return
    }
    setTimeout(() => {
      onReject?.(row.request_id)
    }, FADE_DURATION_MS)
  }, [busy, row.request_id, onReject])

  const initial = row.requester_display_name.slice(0, 1).toUpperCase()
  const isFading = phase === 'reject-fade'
  const isFlashing = phase === 'accept-flash'

  return (
    <div
      data-testid={`friend-request-row-${row.request_id}`}
      data-phase={phase}
      className="row"
      style={{
        // Dynamic-only overrides; the .row recipe owns layout / card / radius.
        ...(isFlashing
          ? { backgroundColor: 'var(--color-cta-confirm)' }
          : null),
        opacity: isFading ? 0 : 1,
        transition: `opacity ${FADE_DURATION_MS}ms ease-out, background-color ${FADE_DURATION_MS}ms ease-out`,
      }}
    >
      <Avatar initial={initial} dataTestid="friend-request-row-avatar" />
      <div
        style={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.requester_display_name}
        </span>
        <span
          data-testid="friend-request-row-sent-at"
          style={{
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-12)',
            color: 'var(--color-text-primary)',
            opacity: 0.7,
          }}
        >
          <FormattedRelativeTime
            value={Math.round((row.created_at - Date.now()) / 60000)}
            unit="minute"
            numeric="auto"
          />
        </span>
      </div>
      <Btn
        kind="leaf"
        size="sm"
        dataCta="friend-request-accept"
        ariaLabel={intl.formatMessage({
          id: 'social.friends.accept_cta',
          defaultMessage: 'Accept',
        })}
        disabled={busy}
        onClick={() => {
          void handleAccept()
        }}
      >
        <FormattedMessage
          id="social.friends.accept_cta"
          defaultMessage="Accept"
        />
      </Btn>
      <Btn
        kind="primary"
        size="sm"
        dataCta="friend-request-reject"
        ariaLabel={intl.formatMessage({
          id: 'social.friends.reject_cta',
          defaultMessage: 'Reject',
        })}
        disabled={busy}
        onClick={() => {
          void handleReject()
        }}
      >
        <FormattedMessage
          id="social.friends.reject_cta"
          defaultMessage="Reject"
        />
      </Btn>
    </div>
  )
}
